import React from 'react';
import { Link } from 'react-router-dom';
import { Rocket, TrendingUp, FileCheck, ArrowRight, Tag } from 'lucide-react';

// Dark navy "How We Help Your Business" — 3 premium cards (matches user's reference screenshot)
const cards = [
  {
    label: 'SEED TO SERIES A', title: 'Startup & Funding Advisory',
    desc: 'Guidance for founders raising their first round — from equity and debt options to pitch decks and investor introductions.',
    bullets: ['Equity & debt funding', 'Pitch deck preparation', 'Investor connections'],
    tags: ['Startup India', 'Seed Fund', 'Angel Networks'],
    cta: 'See funding services', to: '/services/business-loan',
    icon: Rocket, accent: 'text-[#FFB703]', accentBg: 'bg-[#FFB703]/10',
  },
  {
    label: 'GROW ONLINE', title: 'Digital Growth Services',
    desc: 'A full digital toolkit for MSMEs — CRM setup, marketing campaigns, and websites that turn visitors into customers.',
    bullets: ['CRM & automation', 'Digital marketing', 'Website & app development'],
    tags: ['CRM Software', 'SEO', 'Social Media'],
    cta: 'See digital services', to: '/services/digital-marketing',
    icon: TrendingUp, accent: 'text-emerald-400', accentBg: 'bg-emerald-400/10',
  },
  {
    label: 'PAPERWORK HANDLED', title: 'Registration & Compliance',
    desc: 'End-to-end support for company registrations, GST, MSME (Udyam) certificates, and the licenses your business needs.',
    bullets: ['Company incorporation', 'GST & MSME registration', 'Licenses & permits'],
    tags: ['Pvt Ltd', 'LLP', 'OPC'],
    cta: 'See all registrations', to: '/services',
    icon: FileCheck, accent: 'text-slate-300', accentBg: 'bg-white/5',
  },
];

export const HowWeHelp = () => (
  <section className="relative py-20 bg-[#0A1F4A]" data-testid="how-we-help-section">
    <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,183,3,0.10), transparent 40%), radial-gradient(circle at 80% 60%, rgba(36,123,160,0.12), transparent 45%)' }} />
    <div className="container mx-auto px-4 relative">
      <div className="text-center max-w-3xl mx-auto mb-12">
        <span className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white/80 px-4 py-1.5 rounded-full text-xs font-semibold">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FB8500]" /> Services Available
        </span>
        <h2 className="text-3xl md:text-5xl font-black text-white mt-4">
          How We Help <span className="bg-gradient-to-r from-[#FFB703] to-[#FB8500] bg-clip-text text-transparent">Your Business</span>
        </h2>
        <p className="text-slate-300 mt-3 text-sm md:text-base">From funding to compliance, pick the support that fits where you are right now.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {cards.map((c) => {
          const I = c.icon;
          return (
            <div key={c.title} className="relative rounded-2xl bg-[#0F2A5C]/80 backdrop-blur border border-white/10 p-6 hover:border-[#FFB703]/40 transition-all hover:-translate-y-1 hover:shadow-2xl group">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-11 h-11 rounded-xl ${c.accentBg} flex items-center justify-center border border-white/10`}>
                  <I className={c.accent} size={22} />
                </div>
                <span className="text-[10px] font-bold text-white/60 tracking-widest">{c.label}</span>
              </div>
              <h3 className="text-xl font-bold text-white">{c.title}</h3>
              <p className="text-slate-300 text-sm mt-2 leading-relaxed">{c.desc}</p>
              <ul className="mt-4 space-y-1.5">
                {c.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm text-slate-200">
                    <span className={`w-1.5 h-1.5 rounded-full ${c.accent === 'text-[#FFB703]' ? 'bg-[#FFB703]' : c.accent === 'text-emerald-400' ? 'bg-emerald-400' : 'bg-slate-300'}`} />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {c.tags.map((t) => (
                  <span key={t} className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300">{t}</span>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-white/10 rounded-lg bg-white/[0.03] p-3 text-[12px] text-slate-300 flex items-start gap-2">
                <Tag size={13} className="mt-0.5 text-[#FFB703]" />
                <span><b className="text-white">Pricing on enquiry.</b> Quoted in writing after a free call — scope decides the fee, so we won't guess at one here.</span>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <Link to={c.to} className={`inline-flex items-center gap-1 text-sm font-bold ${c.accent} hover:gap-2 transition-all`}>
                  {c.cta} <ArrowRight size={14} />
                </Link>
                <Link to="/contact" className="text-sm font-semibold text-white/70 underline underline-offset-4 hover:text-white transition-colors">
                  Contact for details
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

export default HowWeHelp;
